// packages/core/src/learning/school-modules/credit-optimization.ts
// ─────────────────────────────────────────────────────────────────────────
// LENS PACK — "Credit Optimization" (surface: 'school').
//
// Conforms to LearningModule (LearningTypes.ts) and passes _validateLensPacks:
//   - surface: 'school', homeSchool: 'credit-optimization', no assessment
//   - every lesson lessonKind: 'school-core', exactly 3 role-tagged flashcards
//   - exactly one applyAction per lesson, all live + allowed on 'lens-lesson'
//     (all three use noToolAction — reflection prompts)
//   - lesson ids namespaced `co-*`, flashcard ids `co-*-fc-*` (globally unique)
//   - concepts non-empty, no intra-pack duplicates
//
// Module id / homeSchool match the locked naming convention so the school's
// exhibit nativeModuleId: 'school-credit-optimization' resolves.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_CREDIT_OPTIMIZATION_MODULE: LearningModule = {
	id: 'school-credit-optimization',
	title: 'Credit Optimization',
	description:
		'The worldview that credit is borrowed access with conditions, not extra money. Learn to read a limit as a claim on future income, treat your score as a signal rather than your worth, and keep control ahead of rewards.',
	emoji: '💳',
	accentColor: '#c8ff00',
	tier: 'control',
	pillar: 'debt-credit-interest',
	order: 1,
	surface: 'school',
	homeSchool: 'credit-optimization',
	lessons: [
		// ── Lesson 1 ─────────────────────────────────────────────────────
		{
			id: 'co-access-with-conditions',
			title: 'Credit is access with conditions',
			description:
				'This lesson teaches why the credit optimization lens treats credit as useful access, not free money.',
			pillar: 'debt-credit-interest',
			schoolsOfThought: ['credit-optimization'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['credit', 'borrowing', 'future-claims'],
			prerequisites: [],
			relatedLessons: ['co-score-is-a-signal', 'co-rewards-not-the-game'],
			lessonKind: 'school-core',
			concepts: [
				'access with conditions',
				'borrowing creates claims',
				'credit as reputation',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Credit can feel like extra money because it lets you buy before you fully pay. That feeling is exactly why this lens starts with caution.',
				},
				{
					type: 'paragraph',
					text: 'Credit optimization believes credit is access with conditions. It can help you rent an apartment, finance a car, qualify for better rates, or handle timing gaps, but it also creates claims on your future income.',
				},
				{
					type: 'bullet_list',
					items: [
						'Credit gives access',
						'Credit records behavior',
						'Credit can become expensive fast',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating available credit like available cash. A limit is not money you own. It is money someone is willing to lend you under rules.',
				},
				{
					type: 'example',
					text: 'You have a card with a $5,000 limit. That does not mean you gained $5,000. It means you gained the ability to borrow up to $5,000, with interest, minimum payments, and consequences if the balance gets away from you.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can make credit feel too mechanical. Credit is not only math. It can create stress, temptation, and lifestyle creep if you use it to make a life look affordable before it actually is.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a simple question: what future income have you already promised away? That question matters more than chasing points, limits, or score hacks.',
				},
				{
					type: 'key_takeaway',
					text: 'Credit is not extra money. It is borrowed access that your future self must honor.',
				},
			],
			audioScript:
				'The credit optimization lens starts with a mature idea: credit is useful, but it is not free money. That sounds obvious when you say it slowly, but in real life, credit is designed to feel smoother than cash. You swipe, tap, finance, split payments, accept a limit, or get approved, and the decision feels lighter because the full cost does not hit your bank account right away.\n\nThat delay is the power and the danger. Credit can give you access. It can help you rent a place, set up utilities, finance transportation, build a payment history, handle timing gaps, and qualify for better terms in some situations. Used carefully, it can make adult life easier because other people and institutions often judge your reliability through credit history.\n\nBut the same system can quietly turn into a claim on your future. Every balance is future income that already has a job. Every monthly payment is money that cannot be used for rent, savings, investing, family, travel, moving, quitting a bad job, or building runway. That is why this school pays attention to the hidden cost of borrowing. It is not only the interest rate. It is the loss of flexibility.\n\nA credit card limit is a good example. If your card says you can spend $5,000, it can feel like you have a $5,000 cushion. But that is not the same as having $5,000 saved. Savings gives you options. Borrowed money gives you obligations. If you use the full limit and cannot pay it off quickly, the card starts deciding part of your future before you get there.\n\nCredit optimization is not anti credit. It is anti confusion. It wants you to understand the difference between access and ownership. It wants you to see credit as a system of reputation, timing, incentives, and consequences. The question is not, “How much can I get approved for?” The stronger question is, “How much future income am I comfortable promising away?”\n\nThere is a blind spot though. If this lens becomes only about scores, limits, and approval odds, it can make credit feel like a game. People can start optimizing the scoreboard while ignoring whether the debt makes their real life heavier. A higher score is useful in some contexts, but it is not the same as financial freedom. You can have a good score and still feel trapped by payments.\n\nThe practical move is to slow down before using credit. Ask what the credit is helping you access, what it will cost if the plan goes wrong, and what payment will follow you into next month. If the answer still fits your life, you are making a clearer decision. If the answer creates pressure, then the credit may be offering access at the cost of future options.\n\nCredit is powerful because it opens doors. It is risky because every open door has terms attached.',
			flashcards: [
				{
					id: 'co-access-with-conditions-fc-main',
					role: 'main_idea',
					front: 'What is credit?',
					back: 'Credit is borrowed access with terms, not money you own.',
				},
				{
					id: 'co-access-with-conditions-fc-watch',
					role: 'watch_out',
					front: 'What is the credit limit trap?',
					back: 'Treating available credit like available cash.',
				},
				{
					id: 'co-access-with-conditions-fc-use',
					role: 'use_it',
					front: 'What should you ask first?',
					back: 'Ask what future income this credit would already promise away.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Check your future claims',
				prompt: 'Write down every recurring debt or credit payment and ask what future income it already controls.',
			},
			revision: 1,
		},

		// ── Lesson 2 ─────────────────────────────────────────────────────
		{
			id: 'co-score-is-a-signal',
			title: 'Your score is a signal, not your worth',
			description:
				'This lesson teaches why the credit optimization lens uses credit scores as signals while refusing to confuse them with personal value.',
			pillar: 'debt-credit-interest',
			schoolsOfThought: ['credit-optimization'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['credit-score', 'reliability-signal', 'self-worth'],
			prerequisites: ['co-access-with-conditions'],
			relatedLessons: [
				'co-access-with-conditions',
				'co-rewards-not-the-game',
			],
			lessonKind: 'school-core',
			concepts: [
				'score as signal',
				'reliability record',
				'scoreboard blind spot',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A credit score can feel personal. When the number is low, people feel judged. When the number is high, people can feel like they are winning.',
				},
				{
					type: 'paragraph',
					text: 'Credit optimization sees the score differently. It is a signal used by lenders, landlords, and some institutions. It is not a moral grade, a wealth score, or proof that your life is on track.',
				},
				{
					type: 'bullet_list',
					items: [
						'Payment history matters',
						'Amount owed matters',
						'Time and consistency matter',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is worshiping the score. A score can help you access better terms, but it cannot tell you whether your money life feels free.',
				},
				{
					type: 'example',
					text: 'Someone can have a strong score because they pay on time, but still carry payments that leave them stressed every month. The score says they look reliable to lenders. It does not say they feel calm.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that credit optimization can become too focused on lender approval. That matters, but it is not the whole goal. The real goal is using credit without letting credit run your life.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens asks you to separate two questions: how does the system see you, and how does your real life feel? Both matter, but they are not the same question.',
				},
				{
					type: 'key_takeaway',
					text: 'A credit score can measure lending behavior. It cannot measure your freedom.',
				},
			],
			audioScript:
				'Credit scores are emotionally loaded because they look like a grade. A higher number feels like approval. A lower number can feel like failure. And once people attach identity to the number, credit becomes stressful in a different way. You are no longer just managing a financial signal. You are trying to prove something about yourself.\n\nThe credit optimization lens tries to separate the signal from the shame. A credit score is a tool used by lenders and sometimes landlords or other institutions to estimate how risky it may be to do business with you. It looks at parts of your credit behavior, like whether you pay on time, how much credit you use, how long your accounts have existed, and what kinds of borrowing show up. That information can affect access and pricing. It can matter in real life.\n\nBut a credit score is not your worth. It is not your intelligence. It is not your character in full. It is not a complete picture of your money life. Someone can have a low score because they went through a rough period, helped family, lost income, made mistakes, had medical bills, or simply never learned how the system worked. That does not make them less valuable. It means the system is reading a limited record.\n\nOn the other side, someone can have a strong score and still be financially stressed. They may pay everything on time, but have car payments, card balances, personal loans, subscriptions, and fixed expenses that leave almost no room to breathe. To lenders, they may look reliable. To themselves, they may feel stuck. That is the scoreboard blind spot.\n\nThis school believes the score is useful, but only when it stays in its lane. A score can help you understand how the credit system sees your behavior. It can help you notice patterns. It can help you avoid unnecessary damage from missed payments, high balances, or careless applications. It can help you prepare for moments where credit matters, like renting, buying a car, or applying for a loan.\n\nBut the deeper question is not just, “How do I raise the number?” The deeper question is, “Is my credit life making my real life more flexible or less flexible?” A score can improve while your payments become heavier. A score can recover while your stress is still high. A score can help you access credit, but it cannot tell you whether using that credit is wise for your situation.\n\nThe healthy version of credit optimization is calm and practical. Pay attention to the signal. Learn the rules. Protect your payment history. Keep balances from getting out of control. But do not let a lender facing number become your identity. The score is a measurement inside a system. Your life is bigger than the system.',
			flashcards: [
				{
					id: 'co-score-is-a-signal-fc-main',
					role: 'main_idea',
					front: 'What is a credit score?',
					back: 'A signal lenders use to read parts of your borrowing and payment behavior.',
				},
				{
					id: 'co-score-is-a-signal-fc-watch',
					role: 'watch_out',
					front: 'What is score worship?',
					back: 'Treating a credit score like a moral grade or proof of freedom.',
				},
				{
					id: 'co-score-is-a-signal-fc-use',
					role: 'use_it',
					front: 'What two questions matter?',
					back: 'Ask how the system sees you and how your real life actually feels.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Separate score from stress',
				prompt: 'Write one sentence about how lenders may see your credit, then one sentence about how your credit payments feel in real life.',
			},
			revision: 1,
		},

		// ── Lesson 3 ─────────────────────────────────────────────────────
		{
			id: 'co-rewards-not-the-game',
			title: 'Rewards are not the main game',
			description:
				'This lesson teaches why the credit optimization lens treats rewards, points, and perks as secondary to control.',
			pillar: 'debt-credit-interest',
			schoolsOfThought: ['credit-optimization'],
			estimatedReadMinutes: 2,
			estimatedListenMinutes: 5,
			difficulty: 3,
			tags: ['rewards', 'interest', 'spending-control'],
			prerequisites: ['co-access-with-conditions'],
			relatedLessons: [
				'co-access-with-conditions',
				'co-score-is-a-signal',
			],
			lessonKind: 'school-core',
			concepts: [
				'rewards distraction',
				'interest beats perks',
				'control before optimization',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Credit card rewards can make spending feel strategic. Points, cash back, miles, and sign up bonuses can make a purchase feel smarter than it really is.',
				},
				{
					type: 'paragraph',
					text: 'Credit optimization believes rewards are only useful when the basics are already under control. If interest, fees, or overspending enter the picture, the rewards stop being the win.',
				},
				{
					type: 'bullet_list',
					items: [
						'Points feel exciting',
						'Interest is expensive',
						'Control matters more than perks',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is letting rewards justify spending you would not have made with cash.',
				},
				{
					type: 'example',
					text: 'A person earns $20 in rewards but carries a balance that creates far more than $20 in interest. The reward feels visible. The cost quietly eats the win.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can underweight real benefits for disciplined users. Some people do use rewards well. But the anti trap rule still stands: optimization should never outrun control.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens turns rewards into a test: would you still make the same purchase without points? If not, the reward may be steering the decision instead of serving it.',
				},
				{
					type: 'key_takeaway',
					text: 'Credit rewards are only a win when control is already winning.',
				},
			],
			audioScript:
				'Credit rewards are one of the easiest places for credit optimization to become a trap. Points, cash back, miles, perks, lounge access, bonus categories, and sign up offers can make spending feel smart. You are not just buying something. You are earning. You are optimizing. You are playing the game.\n\nBut the credit optimization lens asks a harder question: who is actually winning the game?\n\nRewards can be useful for people who already have strong control. If someone pays in full, avoids fees, does not overspend, and uses a card for purchases they would have made anyway, then rewards can be a small bonus. The key word is small. It is a bonus attached to controlled behavior. It is not the foundation of the money plan.\n\nThe trap begins when the reward changes the decision. Maybe you spend more because you want to hit a bonus. Maybe you choose a more expensive option because the card has a category multiplier. Maybe you tell yourself a purchase is fine because you are getting points back. Maybe the app makes the reward feel immediate while the bill comes later. At that point, the reward is no longer serving your plan. It is steering it.\n\nInterest makes the math even clearer. If you earn a small amount in rewards but carry a balance, pay late fees, or buy things you would not have bought otherwise, the reward can become a distraction from the real cost. The system shows you the points because points feel good. The interest and balance growth are less exciting, so they are easier to ignore. But they matter more.\n\nThis is why credit optimization says control comes before optimization. Before asking which card gives the best reward, ask whether the card keeps your life simple. Before chasing a bonus, ask whether the spending requirement would pull money away from your real priorities. Before celebrating points, ask whether you would make the same purchase with cash or debit. If the answer is no, the reward is influencing you.\n\nThere is a blind spot in being too strict here. Some people genuinely use rewards well. It would be silly to pretend every point or perk is bad. The problem is not rewards existing. The problem is rewards becoming the emotional excuse for weaker spending decisions.\n\nA clean rule is this: rewards are allowed to improve a decision, but they should not create the decision. If you were already going to buy groceries, a little cash back is fine. If you are buying extra things just to feel like you are optimizing, the credit system may be using your desire to be smart against you.\n\nThe strongest credit users are not the people with the fanciest rewards setup. They are the people who can use credit without losing control, clarity, or future options.',
			flashcards: [
				{
					id: 'co-rewards-not-the-game-fc-main',
					role: 'main_idea',
					front: 'When are rewards useful?',
					back: 'When spending is already controlled and the balance is paid before interest matters.',
				},
				{
					id: 'co-rewards-not-the-game-fc-watch',
					role: 'watch_out',
					front: 'What is the rewards trap?',
					back: 'Letting points or perks justify spending you would not otherwise choose.',
				},
				{
					id: 'co-rewards-not-the-game-fc-use',
					role: 'use_it',
					front: 'What question protects you?',
					back: 'Ask whether you would make the same purchase without rewards.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Run the rewards test',
				prompt: 'Pick one recent card purchase and ask whether you would have made it the same way without points, cash back, or perks.',
			},
			revision: 1,
		},
	],
};
